import { useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import {
  EMAIL_SECURITY_MODES,
  isAppKey,
  type AppKey,
  type EmailSecurityMode,
  type NotificationDeliveryStatus,
} from '@qms360/shared';
import { AppTopBar, Breadcrumbs } from '../../components/AppTopBar.js';
import { PageMessage } from '../AppHome.js';
import { ApiError } from '../../lib/api.js';
import {
  fetchIntegrations,
  fetchOutbox,
  generatePushKeys,
  removeConnectorSecret,
  requeueMessage,
  storeConnectorSecret,
  testConnector,
  updateConnector,
  type ConnectorView,
  type EmailConfigInput,
  type ProjectSyncConfigInput,
  type TestResult,
} from '../../lib/integrations-api.js';
import { t, formatDateTime } from '../../lib/i18n.js';

/**
 * The Integration Cockpit (spec §13.2), reached from each app's Settings.
 *
 * What it is careful to say:
 *  - **What is not connected.** A connector that is only an extension point or
 *    a stub says so, with the reason, and has no switch.
 *  - **What "sent" means.** Accepted by the mail server — never "delivered".
 *  - **What happens to mail composed while email was off.** It stays in the
 *    outbox, marked not sent, and is sent only if somebody sends it again.
 */

const HEALTH_LABELS: Record<string, { label: string; tone: string }> = {
  connected: { label: 'Connected', tone: 'var(--tone-positive)' },
  degraded: { label: 'Not checked yet', tone: 'var(--tone-pending)' },
  failed: { label: 'Failing', tone: 'var(--tone-critical)' },
  disabled: { label: 'Off', tone: 'var(--tone-neutral)' },
};

const AVAILABILITY_LABELS: Record<ConnectorView['availability'], string> = {
  available: '',
  extension_point: 'Extension point',
  stub: 'Not connected',
  later: 'Not available yet',
};

const SECURITY_LABELS: Record<EmailSecurityMode, string> = {
  tls: 'TLS (usually port 465)',
  starttls: 'STARTTLS (usually port 587)',
  none: 'None — unencrypted, for a relay inside your network only',
};

export const STATUS_LABELS: Record<NotificationDeliveryStatus, string> = {
  pending: 'Queued',
  sent: 'Sent — accepted by the mail server',
  failed: 'Failed',
  recorded_not_sent: 'Not sent',
  suppressed: 'Switched off by the reader',
};

function when(iso: string | null): string {
  return iso ? formatDateTime(iso, { dateStyle: 'medium', timeStyle: 'short' }) : '—';
}

export function Integrations() {
  const params = useParams<{ app: string }>();
  const integrations = useQuery({ queryKey: ['integrations'], queryFn: fetchIntegrations, retry: false });

  const app = params.app;
  if (!app || !isAppKey(app)) {
    return <PageMessage title={t("Unknown application")} body="That application does not exist." />;
  }
  const data = integrations.data;

  return (
    <div className="min-h-full">
      <AppTopBar app={app as AppKey} />
      <Breadcrumbs app={app as AppKey} trail={['Settings', 'Integration Cockpit']} />

      <main className="mx-auto max-w-5xl px-4 pb-16 pt-4">
        <h1 className="m-0 text-2xl font-semibold">{t("Integration Cockpit")}</h1>
        <p className="mt-1 text-sm text-content-secondary">
          {t("Connections to other systems. Shared by all three applications.")}
        </p>

        {integrations.isLoading && <p className="mt-6 text-content-secondary">{t("Loading…")}</p>}
        {integrations.error && (
          <section className="mt-6 rounded-md border border-border bg-tone-info-surface p-6" role="status">
            <h2 className="m-0 text-base font-semibold">{t("Not available to your role")}</h2>
            <p className="mb-0 mt-2 text-sm text-content-secondary">
              {t("The Integration Cockpit needs integration authority. Ask an administrator if you need it.")}
            </p>
          </section>
        )}

        {data && (
          <>
            {!data.canChange && (
              <p className="mt-4 rounded bg-tone-info-surface p-3 text-sm" role="status">
                {t("You can see these connections but not change them: they are shared by all three applications, so changing one needs integration authority in all three.")}
              </p>
            )}

            <ul className="m-0 mt-6 list-none space-y-4 p-0">
              {data.connectors.map((connector) =>
                connector.type === 'email' ? (
                  <EmailCard
                    key={connector.type}
                    connector={connector}
                    canChange={data.canChange}
                    vaultAvailable={data.vaultAvailable}
                  />
                ) : connector.type === 'push' ? (
                  <PushCard key={connector.type} connector={connector} canChange={data.canChange} />
                ) : connector.type === 'identity_sync' ? (
                  <ProjectSyncCard
                    key={connector.type}
                    app={app as AppKey}
                    connector={connector}
                    canChange={data.canChange}
                    vaultAvailable={data.vaultAvailable}
                  />
                ) : (
                  <ConnectorCard key={connector.type} connector={connector} />
                ),
              )}
              {data.builtIn.map((entry) => (
                <li key={entry.key} className="rounded-md border border-border p-4 text-sm">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <strong>{t(entry.label)}</strong>
                    <span className="text-xs text-content-secondary">{t("Built in")}</span>
                  </div>
                  <p className="mb-0 mt-1 text-content-secondary">{entry.description}</p>
                  {entry.settingsPath && (
                    <Link to={`/${app}/${entry.settingsPath}`} className="mt-2 inline-block text-ag-purple">
                      {t("Open AI Settings")}
                    </Link>
                  )}
                </li>
              ))}
            </ul>

            <Outbox />
          </>
        )}
      </main>
    </div>
  );
}

function HealthBadge({ connector }: { connector: ConnectorView }) {
  const health = HEALTH_LABELS[connector.connector?.health ?? 'disabled']!;
  return (
    <span className="text-xs font-semibold" style={{ color: health.tone }} data-testid={`health-${connector.type}`}>
      {t(health.label)}
    </span>
  );
}

function ConnectorCard({ connector }: { connector: ConnectorView }) {
  return (
    <li className="rounded-md border border-border p-4 text-sm" data-testid={`connector-${connector.type}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <strong>{t(connector.label)}</strong>
        <span className="text-xs text-content-secondary">{t(AVAILABILITY_LABELS[connector.availability])}</span>
      </div>
      <p className="mb-0 mt-1 text-content-secondary">{connector.description}</p>
      {connector.unavailableReason && <p className="mb-0 mt-2">{connector.unavailableReason}</p>}
    </li>
  );
}

function configOf(connector: ConnectorView): EmailConfigInput {
  const config = (connector.connector?.config ?? {}) as Partial<EmailConfigInput>;
  return {
    host: config.host ?? '',
    port: config.port ?? 587,
    security: config.security ?? 'starttls',
    username: config.username ?? null,
    fromAddress: config.fromAddress ?? '',
    fromName: config.fromName ?? null,
    sandboxRecipient: config.sandboxRecipient ?? null,
  };
}

function EmailCard({
  connector,
  canChange,
  vaultAvailable,
}: {
  connector: ConnectorView;
  canChange: boolean;
  vaultAvailable: boolean;
}) {
  const queryClient = useQueryClient();
  const [config, setConfig] = useState<EmailConfigInput>(() => configOf(connector));
  const [secret, setSecret] = useState('');
  const [sendTestMessage, setSendTestMessage] = useState(false);
  const [result, setResult] = useState<TestResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = () => {
    setError(null);
    void queryClient.invalidateQueries({ queryKey: ['integrations'] });
    void queryClient.invalidateQueries({ queryKey: ['outbox'] });
  };
  const fail = (fallback: string) => (caught: unknown) =>
    setError(caught instanceof ApiError ? caught.message : fallback);

  const clean = (): EmailConfigInput => ({
    ...config,
    username: config.username?.trim() || null,
    fromName: config.fromName?.trim() || null,
    sandboxRecipient: config.sandboxRecipient?.trim() || null,
  });

  const save = useMutation({
    mutationFn: (body: { config?: EmailConfigInput; isEnabled?: boolean; isSandbox?: boolean }) =>
      updateConnector('email', body),
    onSuccess: refresh,
    onError: fail(t("The email settings could not be saved.")),
  });
  const storeSecret = useMutation({
    mutationFn: () => storeConnectorSecret('email', secret),
    onSuccess: () => {
      setSecret('');
      refresh();
    },
    onError: fail(t("The password could not be stored.")),
  });
  const dropSecret = useMutation({
    mutationFn: () => removeConnectorSecret('email'),
    onSuccess: refresh,
    onError: fail(t("The password could not be removed.")),
  });
  const test = useMutation({
    mutationFn: () => testConnector('email', sendTestMessage),
    onSuccess: (outcome) => {
      setResult(outcome);
      refresh();
    },
    onError: fail(t("The test could not be run.")),
  });

  const row = connector.connector;
  const disabled = !canChange;
  const field = (key: keyof EmailConfigInput) => ({
    value: (config[key] ?? '') as string | number,
    disabled,
    onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
      setConfig({ ...config, [key]: key === 'port' ? Number(event.target.value) : event.target.value }),
    className: 'rounded border border-border px-2 py-1',
  });

  return (
    <li className="rounded-md border border-border p-4 text-sm" data-testid="connector-email">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <strong>{t(connector.label)}</strong>
        <HealthBadge connector={connector} />
      </div>
      <p className="mb-0 mt-1 text-content-secondary">{connector.description}</p>
      {row?.lastHealthMessage && (
        <p className="mb-0 mt-2 text-xs text-content-secondary" data-testid="health-message">
          {row.lastHealthMessage}
          {row.lastHealthCheckAt ? t(" (checked {0})", { 0: when(row.lastHealthCheckAt) }) : ''}
        </p>
      )}
      {row?.isSandbox && (
        <p className="mb-0 mt-2 rounded bg-tone-pending-surface p-2 text-xs" role="status">
          {t("Sandbox is on: every message goes to")}{' '}{String(row.config.sandboxRecipient ?? '')}{t(", marked [SANDBOX].")}
        </p>
      )}

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1">
          <span className="text-content-secondary">{t("Mail server")}</span>
          <input type="text" placeholder={t("smtp.example.com")} {...field('host')} />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-content-secondary">{t("Port")}</span>
          <input type="number" min={1} max={65535} {...field('port')} />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-content-secondary">{t("Security")}</span>
          <select
            value={config.security}
            disabled={disabled}
            onChange={(event) => setConfig({ ...config, security: event.target.value as EmailSecurityMode })}
            className="rounded border border-border px-2 py-1"
          >
            {EMAIL_SECURITY_MODES.map((mode) => (
              <option key={mode} value={mode}>
                {t(SECURITY_LABELS[mode])}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-content-secondary">{t("User name (empty if the server needs none)")}</span>
          <input type="text" autoComplete="off" {...field('username')} />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-content-secondary">{t("Send as (address)")}</span>
          <input type="email" placeholder={t("qms360@example.com")} {...field('fromAddress')} />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-content-secondary">{t("Send as (name)")}</span>
          <input type="text" placeholder={t("QMS360")} {...field('fromName')} />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-content-secondary">{t("Sandbox address")}</span>
          <input type="email" placeholder={t("Every message goes here in sandbox mode")} {...field('sandboxRecipient')} />
        </label>
      </div>

      {canChange && (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <button
            type="button"
            disabled={save.isPending}
            onClick={() => save.mutate({ config: clean() })}
            className="rounded bg-ag-purple px-3 py-1.5 text-white disabled:opacity-60"
          >
            {t("Save settings")}
          </button>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={row?.isEnabled ?? false}
              disabled={save.isPending}
              onChange={(event) => save.mutate({ config: clean(), isEnabled: event.target.checked })}
            />
            {t("Email switched on")}
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={row?.isSandbox ?? false}
              disabled={save.isPending}
              onChange={(event) => save.mutate({ config: clean(), isSandbox: event.target.checked })}
            />
            {t("Sandbox")}
          </label>
        </div>
      )}

      <div className="mt-4 border-t border-border pt-3">
        <p className="m-0 text-content-secondary">
          {connector.secretLabel}:{' '}
          {connector.secret?.set ? (
            <strong data-testid="secret-hint">{t("stored, ending …")}{connector.secret.hint}</strong>
          ) : (
            <strong>{t("not stored")}</strong>
          )}
          {connector.secret?.unreadable && (
            <span style={{ color: 'var(--tone-rejected)' }}>{' '}{t("— sealed under a key this server no longer has; enter it again")}</span>
          )}
        </p>
        {canChange && vaultAvailable && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <label className="sr-only" htmlFor="smtp-secret">
              {connector.secretLabel}
            </label>
            <input
              id="smtp-secret"
              type="password"
              autoComplete="new-password"
              value={secret}
              onChange={(event) => setSecret(event.target.value)}
              className="rounded border border-border px-2 py-1"
            />
            <button
              type="button"
              disabled={!secret || storeSecret.isPending}
              onClick={() => storeSecret.mutate()}
              className="rounded border border-border px-3 py-1 disabled:opacity-60"
            >
              {t("Store password")}
            </button>
            {connector.secret?.set && (
              <button
                type="button"
                disabled={dropSecret.isPending}
                onClick={() => dropSecret.mutate()}
                className="rounded border border-border px-3 py-1 disabled:opacity-60"
              >
                {t("Remove")}
              </button>
            )}
            <span className="text-xs text-content-secondary">{t("Never shown again once stored.")}</span>
          </div>
        )}
        {canChange && !vaultAvailable && (
          <p className="mb-0 mt-2 text-xs" style={{ color: 'var(--tone-rejected)' }}>
            {t("Passwords cannot be stored: the credential vault is not configured on this server.")}
          </p>
        )}
      </div>

      {canChange && (
        <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-border pt-3">
          <button
            type="button"
            disabled={test.isPending}
            onClick={() => test.mutate()}
            className="rounded border border-border px-3 py-1.5 disabled:opacity-60"
          >
            {test.isPending ? t("Testing…") : t("Test connection")}
          </button>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={sendTestMessage} onChange={(event) => setSendTestMessage(event.target.checked)} />
            {t("Also send me a test message")}
          </label>
        </div>
      )}
      {result && (
        <div className="mt-2 text-sm" role="status" data-testid="test-result">
          <p className="m-0">{result.message}</p>
          {result.testMessage && (
            <p className="mb-0 mt-1">
              {t("Test message:")}{' '}{result.testMessage.sent ? t("accepted by the mail server for {0}", { 0: result.testMessage.to }) : result.testMessage.message}
            </p>
          )}
        </div>
      )}
      {error && (
        <p role="alert" className="mb-0 mt-2 text-sm" style={{ color: 'var(--tone-rejected)' }}>
          {error}
        </p>
      )}
    </li>
  );
}

function PushCard({ connector, canChange }: { connector: ConnectorView; canChange: boolean }) {
  const queryClient = useQueryClient();
  const row = connector.connector;
  const [subject, setSubject] = useState(String(row?.config.subject ?? ''));
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasKeys = Boolean(row?.config.publicKey) && Boolean(connector.secret?.set);

  const refresh = () => {
    setError(null);
    setConfirming(false);
    void queryClient.invalidateQueries({ queryKey: ['integrations'] });
  };
  const fail = (fallback: string) => (caught: unknown) =>
    setError(caught instanceof ApiError ? caught.message : fallback);

  const save = useMutation({
    mutationFn: (body: { isEnabled?: boolean }) =>
      updateConnector('push', { config: { subject }, ...body }),
    onSuccess: refresh,
    onError: fail(t("The push settings could not be saved.")),
  });
  const keys = useMutation({ mutationFn: generatePushKeys, onSuccess: refresh, onError: fail(t("The keys could not be generated.")) });

  return (
    <li className="rounded-md border border-border p-4 text-sm" data-testid="connector-push">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <strong>{t(connector.label)}</strong>
        <HealthBadge connector={connector} />
      </div>
      <p className="mb-0 mt-1 text-content-secondary">{connector.description}</p>
      <p className="mb-0 mt-2" data-testid="push-keys">
        {t("Signing keys:")}{' '}{hasKeys ? <strong>{t("generated")}</strong> : <strong>{t("not generated yet")}</strong>}
      </p>

      <label className="mt-3 flex flex-col gap-1 sm:max-w-md">
        <span className="text-content-secondary">{t("Contact for the push services (mailto: or https: link)")}</span>
        <input
          type="text"
          value={subject}
          disabled={!canChange}
          placeholder={t("mailto:it@example.com")}
          onChange={(event) => setSubject(event.target.value)}
          className="rounded border border-border px-2 py-1"
        />
      </label>

      {canChange && (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <button
            type="button"
            disabled={save.isPending}
            onClick={() => save.mutate({})}
            className="rounded bg-ag-purple px-3 py-1.5 text-white disabled:opacity-60"
          >
            {t("Save settings")}
          </button>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={row?.isEnabled ?? false}
              disabled={save.isPending || !hasKeys}
              onChange={(event) => save.mutate({ isEnabled: event.target.checked })}
            />
            {t("Push switched on")}
          </label>
          {!confirming ? (
            <button type="button" onClick={() => setConfirming(true)} className="rounded border border-border px-3 py-1.5">
              {hasKeys ? t("Replace signing keys") : t("Generate signing keys")}
            </button>
          ) : (
            <span className="flex flex-wrap items-center gap-2" role="status">
              {hasKeys && <span>{t("Every device switched on now will be switched off, and people must turn push on again.")}</span>}
              <button
                type="button"
                disabled={keys.isPending}
                onClick={() => keys.mutate()}
                className="rounded bg-ag-purple px-3 py-1.5 text-white disabled:opacity-60"
              >
                {hasKeys ? t("Replace them") : t("Generate")}
              </button>
              <button type="button" onClick={() => setConfirming(false)} className="rounded border border-border px-3 py-1.5">
                {t("Cancel")}
              </button>
            </span>
          )}
        </div>
      )}
      {error && (
        <p role="alert" className="mb-0 mt-2 text-sm" style={{ color: 'var(--tone-critical)' }}>
          {error}
        </p>
      )}
      <p className="mb-0 mt-3 text-xs text-content-secondary">
        {t("Each person switches push on for each of their devices, from Notifications. Messages are encrypted for the device; the browser’s push service carries them but cannot read them.")}
      </p>
    </li>
  );
}

function ProjectSyncCard({
  app,
  connector,
  canChange,
  vaultAvailable,
}: {
  app: AppKey;
  connector: ConnectorView;
  canChange: boolean;
  vaultAvailable: boolean;
}) {
  const queryClient = useQueryClient();
  const row = connector.connector;
  const stored = (row?.config ?? {}) as Partial<ProjectSyncConfigInput>;
  const [config, setConfig] = useState<ProjectSyncConfigInput>({
    source: stored.source ?? 'sample',
    url: stored.url ?? null,
    recordsPath: stored.recordsPath ?? null,
    externalSource: stored.externalSource ?? 'algihaz-platform',
    scheduleIntervalMinutes: stored.scheduleIntervalMinutes ?? null,
  });
  const [token, setToken] = useState('');
  const [error, setError] = useState<string | null>(null);

  const refresh = () => {
    setError(null);
    void queryClient.invalidateQueries({ queryKey: ['integrations'] });
  };
  const fail = (fallback: string) => (caught: unknown) =>
    setError(caught instanceof ApiError ? caught.message : fallback);

  const clean = (): ProjectSyncConfigInput => ({
    ...config,
    url: config.source === 'url' ? config.url?.trim() || null : null,
    recordsPath: config.recordsPath?.trim() || null,
  });
  const save = useMutation({
    mutationFn: (body: { isEnabled?: boolean; isSandbox?: boolean }) =>
      updateConnector('identity_sync', { config: clean(), ...body }),
    onSuccess: refresh,
    onError: fail(t("The sync settings could not be saved.")),
  });
  const storeToken = useMutation({
    mutationFn: () => storeConnectorSecret('identity_sync', token),
    onSuccess: () => {
      setToken('');
      refresh();
    },
    onError: fail(t("The token could not be stored.")),
  });

  return (
    <li className="rounded-md border border-border p-4 text-sm" data-testid="connector-identity_sync">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <strong>{t(connector.label)}</strong>
        <span className="text-xs text-content-secondary">
          {row?.isEnabled ? t("On") : t("Off")}
          {row?.isSandbox ? t(" · sandbox: dry runs only") : ''}
        </span>
      </div>
      <p className="mb-0 mt-1 text-content-secondary">{connector.description}</p>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1">
          <span className="text-content-secondary">{t("Feed")}</span>
          <select
            value={config.source}
            disabled={!canChange}
            onChange={(event) => setConfig({ ...config, source: event.target.value as ProjectSyncConfigInput['source'] })}
            className="rounded border border-border px-2 py-1"
          >
            <option value="sample">{t("Built-in sample (for trying it out)")}</option>
            <option value="url">{t("The platform's feed, at an address")}</option>
          </select>
        </label>
        {config.source === 'url' && (
          <label className="flex flex-col gap-1">
            <span className="text-content-secondary">{t("Feed address")}</span>
            <input
              type="url"
              value={config.url ?? ''}
              disabled={!canChange}
              onChange={(event) => setConfig({ ...config, url: event.target.value })}
              className="rounded border border-border px-2 py-1"
            />
          </label>
        )}
        <label className="flex flex-col gap-1">
          <span className="text-content-secondary">{t("Where the list is in the response (dot path; empty = the whole response)")}</span>
          <input
            type="text"
            value={config.recordsPath ?? ''}
            disabled={!canChange}
            placeholder={t("data.items")}
            onChange={(event) => setConfig({ ...config, recordsPath: event.target.value })}
            className="rounded border border-border px-2 py-1 font-mono text-xs"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-content-secondary">{t("Recorded as coming from")}</span>
          <input
            type="text"
            value={config.externalSource}
            disabled={!canChange}
            onChange={(event) => setConfig({ ...config, externalSource: event.target.value })}
            className="rounded border border-border px-2 py-1"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-content-secondary">{t("Run by itself every (minutes; empty = only by hand)")}</span>
          <input
            type="number"
            min={15}
            value={config.scheduleIntervalMinutes ?? ''}
            disabled={!canChange}
            onChange={(event) =>
              setConfig({ ...config, scheduleIntervalMinutes: event.target.value ? Number(event.target.value) : null })
            }
            className="rounded border border-border px-2 py-1"
          />
        </label>
      </div>

      {canChange && (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <button
            type="button"
            disabled={save.isPending}
            onClick={() => save.mutate({})}
            className="rounded bg-ag-purple px-3 py-1.5 text-white disabled:opacity-60"
          >
            {t("Save settings")}
          </button>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={row?.isEnabled ?? false}
              disabled={save.isPending || !row}
              onChange={(event) => save.mutate({ isEnabled: event.target.checked })}
            />
            {t("Sync switched on")}
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={row?.isSandbox ?? false}
              disabled={save.isPending || !row}
              onChange={(event) => save.mutate({ isSandbox: event.target.checked })}
            />
            {t("Sandbox (dry runs only)")}
          </label>
        </div>
      )}
      <p className="mb-0 mt-2 text-xs text-content-secondary">
        {t("A scheduled run acts as whoever switched the sync on, and stops if they lose the authority to change project master data. Runs, errors and the field mapping are in the")}{' '}
        <Link to={`/${app}/settings/data-sync`} className="text-ag-purple">
          {t("Data Sync Cockpit")}
        </Link>
        .
      </p>

      {connector.secretLabel && (
        <div className="mt-3 border-t border-border pt-3">
          <p className="m-0 text-content-secondary">
            {connector.secretLabel}: <strong>{connector.secret?.set ? t("stored, ending …{0}", { 0: connector.secret.hint }) : t("not stored")}</strong>
          </p>
          {canChange && vaultAvailable && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <label className="sr-only" htmlFor="sync-token">
                {connector.secretLabel}
              </label>
              <input
                id="sync-token"
                type="password"
                autoComplete="off"
                value={token}
                onChange={(event) => setToken(event.target.value)}
                className="rounded border border-border px-2 py-1"
              />
              <button
                type="button"
                disabled={!token || storeToken.isPending}
                onClick={() => storeToken.mutate()}
                className="rounded border border-border px-3 py-1 disabled:opacity-60"
              >
                {t("Store token")}
              </button>
            </div>
          )}
        </div>
      )}
      {error && (
        <p role="alert" className="mb-0 mt-2 text-sm" style={{ color: 'var(--tone-critical)' }}>
          {error}
        </p>
      )}
    </li>
  );
}

function Outbox() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<NotificationDeliveryStatus | ''>('');
  const [error, setError] = useState<string | null>(null);

  const outbox = useInfiniteQuery({
    queryKey: ['outbox', status],
    queryFn: ({ pageParam }) => fetchOutbox({ status, before: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    retry: false,
  });

  const requeue = useMutation({
    mutationFn: requeueMessage,
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['outbox'] });
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : t("The message could not be queued.")),
  });

  const first = outbox.data?.pages[0];
  const messages = outbox.data?.pages.flatMap((page) => page.messages) ?? [];

  return (
    <section className="mt-10" aria-labelledby="outbox-heading">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 id="outbox-heading" className="m-0 text-lg font-semibold">
          {t("Email outbox")}
        </h2>
        <label className="flex items-center gap-2 text-sm">
          <span className="text-content-secondary">{t("Show")}</span>
          <select
            value={status}
            onChange={(event) => setStatus(event.target.value as NotificationDeliveryStatus | '')}
            className="rounded border border-border px-2 py-1"
          >
            <option value="">{t("Everything")}</option>
            {(Object.keys(STATUS_LABELS) as NotificationDeliveryStatus[]).map((key) => (
              <option key={key} value={key}>
                {t(STATUS_LABELS[key])}
              </option>
            ))}
          </select>
        </label>
      </div>

      {first && (
        <>
          <p className="mt-2 text-sm text-content-secondary" data-testid="outbox-counts">
            {first.counts.pending}{' '}{t("queued ·")}{' '}{first.counts.notSent}{' '}{t("not sent ·")}{' '}{first.counts.failed}{' '}{t("failed")}
          </p>
          {!first.emailReady && (
            <p className="mt-2 rounded bg-tone-pending-surface p-3 text-sm" role="status" data-testid="outbox-not-ready">
              {first.notReadyReason}{' '}{t("Email is composed and kept here, marked not sent. It is not sent later by itself: once email is set up, send a message again from this list if it still matters.")}
            </p>
          )}
        </>
      )}
      {error && (
        <p role="alert" className="mt-2 text-sm" style={{ color: 'var(--tone-rejected)' }}>
          {error}
        </p>
      )}
      {outbox.error && <p className="mt-2 text-sm text-content-secondary">{t("The outbox is not available to your role.")}</p>}

      {messages.length > 0 && (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="text-start text-content-secondary">
                <th className="border-b border-border p-2 text-start font-normal">{t("Composed")}</th>
                <th className="border-b border-border p-2 text-start font-normal">{t("To")}</th>
                <th className="border-b border-border p-2 text-start font-normal">{t("Subject")}</th>
                <th className="border-b border-border p-2 text-start font-normal">{t("Status")}</th>
                <th className="border-b border-border p-2" />
              </tr>
            </thead>
            <tbody>
              {messages.map((message) => (
                <tr key={message.id} data-testid="outbox-row">
                  <td className="border-b border-border p-2 align-top">{when(message.createdAt)}</td>
                  <td className="border-b border-border p-2 align-top">
                    {message.recipientName}
                    <div className="text-xs text-content-secondary">{message.recipientEmail}</div>
                    {message.deliveredTo && message.deliveredTo !== message.recipientEmail && (
                      <div className="text-xs">{t("went to")}{' '}{message.deliveredTo}</div>
                    )}
                  </td>
                  <td className="border-b border-border p-2 align-top">{message.subject}</td>
                  <td className="border-b border-border p-2 align-top">
                    {t(STATUS_LABELS[message.deliveryStatus])}
                    {message.sentAt && <div className="text-xs text-content-secondary">{when(message.sentAt)}</div>}
                    {message.failureReason && message.deliveryStatus !== 'sent' && (
                      <div className="text-xs text-content-secondary">{message.failureReason}</div>
                    )}
                  </td>
                  <td className="border-b border-border p-2 align-top">
                    {first?.canRequeue &&
                      first.emailReady &&
                      (message.deliveryStatus === 'recorded_not_sent' || message.deliveryStatus === 'failed') && (
                        <button
                          type="button"
                          disabled={requeue.isPending}
                          onClick={() => requeue.mutate(message.id)}
                          className="rounded border border-border px-2 py-1 text-xs disabled:opacity-60"
                        >
                          {t("Send again")}
                        </button>
                      )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!outbox.isLoading && !outbox.error && messages.length === 0 && (
        <p className="mt-3 text-sm text-content-secondary">{t("No email yet.")}</p>
      )}
      {outbox.hasNextPage && (
        <button
          type="button"
          disabled={outbox.isFetchingNextPage}
          onClick={() => void outbox.fetchNextPage()}
          className="mt-3 rounded border border-border px-3 py-1.5 text-sm disabled:opacity-60"
        >
          {t("Older")}
        </button>
      )}
    </section>
  );
}
